import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------
// GRADIENT DEFINITIONS FOR THEME
// ----------------------------------------------------------------------

/**
 * Primary gradient (Purple to Blue)
 * Used for: Headers, primary buttons, important CTAs
 */
export const primaryGradient = {
  main: 'linear-gradient(135deg, #A8469B 0%, #569cd3 100%)',
  light: 'linear-gradient(135deg, #C668B7 0%, #7DB8E5 100%)',
  dark: 'linear-gradient(135deg, #8e3983 0%, #4788ba 100%)',
  reverse: 'linear-gradient(135deg, #569cd3 0%, #A8469B 100%)',
};

/**
 * Success gradient (Light Green to Green)
 * Used for: Completed states, success messages
 */
export const successGradient = {
  main: 'linear-gradient(135deg, #AAF27F 0%, #54D62C 100%)',
  light: 'linear-gradient(135deg, #C4F7A7 0%, #76E04E 100%)',
  dark: 'linear-gradient(135deg, #8BD367 0%, #229A16 100%)',
};

/**
 * Info gradient (Light Blue to Blue)
 * Used for: Information cards, notifications
 */
export const infoGradient = {
  main: 'linear-gradient(135deg, #74CAFF 0%, #1890FF 100%)',
  light: 'linear-gradient(135deg, #A0DDFF 0%, #4AA9FF 100%)',
  dark: 'linear-gradient(135deg, #3DA0E8 0%, #0C53B7 100%)',
};

/**
 * Warning gradient (Light Yellow to Orange)
 * Used for: In-progress states, warnings
 */
export const warningGradient = {
  main: 'linear-gradient(135deg, #FFE16A 0%, #FFC107 100%)',
  light: 'linear-gradient(135deg, #FFEE9F 0%, #FFD54F 100%)',
  dark: 'linear-gradient(135deg, #FFD740 0%, #B78103 100%)',
};

/**
 * Error gradient (Light Red to Red)
 * Used for: Error states, destructive actions
 */
export const errorGradient = {
  main: 'linear-gradient(135deg, #FFA48D 0%, #FF4842 100%)',
  light: 'linear-gradient(135deg, #FFB8A8 0%, #FF6B66 100%)',
  dark: 'linear-gradient(135deg, #FF7A6F 0%, #B72136 100%)',
};

/**
 * Glassmorphism backgrounds
 * Semi-transparent backgrounds with blur
 */
export const glassBackgrounds = (color, opacity = 0.15) => ({
  background: alpha(color, opacity),
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)', // Safari support
});

/**
 * Gradient text effect
 */
export const gradientText = (gradient) => ({
  background: gradient,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
});

/**
 * Create colored glass card background
 */
export const createGlassCard = (color) => ({
  background: `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`,
  border: `2px solid ${alpha(color, 0.2)}`,
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: `0 8px 24px ${alpha(color, 0.2)}`,
    borderColor: alpha(color, 0.4),
  },
});

/**
 * Animated gradient border
 */
export const gradientBorder = (gradient, width = 2) => ({
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: 'inherit',
    padding: `${width}px`,
    background: gradient,
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMaskComposite: 'xor',
    maskComposite: 'exclude',
  },
});

/**
 * Floating animation keyframes
 */
export const floatingAnimation = {
  '@keyframes float': {
    '0%, 100%': {
      transform: 'translateY(0px)',
    },
    '50%': {
      transform: 'translateY(-10px)',
    },
  },
};

/**
 * Gradient shimmer effect
 */
export const shimmerEffect = {
  '@keyframes shimmer': {
    '0%': {
      backgroundPosition: '200% center',
    },
    '100%': {
      backgroundPosition: '-200% center',
    },
  },
  animation: 'shimmer 3s ease-in-out infinite',
  backgroundSize: '200% auto',
};

/**
 * Pulse glow effect
 */
export const pulseGlow = (color) => ({
  '@keyframes pulse-glow': {
    '0%, 100%': {
      boxShadow: `0 0 10px ${alpha(color, 0.3)}`,
    },
    '50%': {
      boxShadow: `0 0 20px ${alpha(color, 0.6)}`,
    },
  },
  animation: 'pulse-glow 2s ease-in-out infinite',
});

export default {
  primaryGradient,
  successGradient,
  infoGradient,
  warningGradient,
  errorGradient,
  glassBackgrounds,
  gradientText,
  createGlassCard,
  gradientBorder,
  floatingAnimation,
  shimmerEffect,
  pulseGlow,
};
